import * as React from 'react';
import { cn } from '../lib/utils';

export interface ListDetailLayoutProps {
  layout: 'split' | 'panel';
  selectedId?: string | null;
  onSelectItem?: (id: string | null) => void;
  listContent: React.ReactNode;
  detailContent?: React.ReactNode;
  listWidth?: string;
  emptyDetailState?: React.ReactNode;
  panelWidth?: string;
  showBackdrop?: boolean;
  className?: string;
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

const defaultEmptyDetail = (
  <div className="flex h-full items-center justify-center">
    <p className="text-body text-muted-foreground">Select an item to view details</p>
  </div>
);

export function ListDetailLayout({
  layout,
  selectedId,
  onSelectItem,
  listContent,
  detailContent,
  listWidth = '400px',
  emptyDetailState,
  panelWidth = '50%',
  showBackdrop: _showBackdrop = false,
  className,
}: ListDetailLayoutProps) {
  const hasSelection = selectedId != null;
  const emptyDetail = emptyDetailState ?? defaultEmptyDetail;

  // Panel mode — Escape key closes the panel. Bail when a modal (e.g. a
  // Radix Dialog rendered over the detail panel, such as
  // ProviderDefaultsDialog) is open: that dialog's own onOpenChange already
  // consumes the Escape via its DismissableLayer, which runs in the capture
  // phase before this bubble-phase listener — closing the whole panel TOO
  // would dismiss both layers on one keypress. `[role="dialog"]` is the
  // standard ARIA marker Radix's Dialog.Content renders while open, so this
  // check works for any overlay library, not just a specific component.
  React.useEffect(() => {
    if (layout === 'split') return;
    if (!hasSelection) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="dialog"]')) return;
      onSelectItem?.(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [layout, hasSelection, onSelectItem]);

  if (layout === 'split') {
    return (
      <div className={cn('flex h-full', className)}>
        <div className="shrink-0 overflow-y-auto border-r" style={{ width: listWidth }}>
          {listContent}
        </div>
        <div className="flex-1 overflow-y-auto">{hasSelection && detailContent ? detailContent : emptyDetail}</div>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full', className)}>
      <div className="h-full flex-1 min-w-0 overflow-y-auto">{listContent}</div>
      {hasSelection && (
        <div
          className="relative h-full shrink-0 overflow-y-auto border-l bg-background shadow-xl animate-in slide-in-from-right"
          style={{ width: panelWidth }}
        >
          <button
            type="button"
            className="absolute right-3 top-3 z-10 rounded-sm p-1 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            onClick={() => onSelectItem?.(null)}
            aria-label="Close panel"
          >
            <CloseIcon />
          </button>
          <div className="pt-10">{detailContent}</div>
        </div>
      )}
    </div>
  );
}
